import { useState } from "react";
import { Phone, MessageCircle, Mail, Instagram, MapPin } from "lucide-react";
import { toast } from "sonner";
import { BRANCHES } from "@/lib/data";
import { CLASS_COORDINATORS, ALT_CLASS_COORDINATORS, BRANCH_EMAILS, buildMailto } from "@/lib/whatsapp";

const DEFAULT_WHATSAPP = "918248214953"; // Fallback to Phoenix India coordinator

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", branch: "", program: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submitToWhatsApp = (e, useAlt = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);

    const lines = [
      "*New enquiry — Phoenix Dance Academy*",
      "",
      `*Name:* ${form.name}`,
      `*Email:* ${form.email}`,
      form.phone ? `*Phone:* ${form.phone}` : null,
      form.branch ? `*Branch of interest:* ${form.branch}` : null,
      form.program ? `*Program of interest:* ${form.program}` : null,
      "",
      "*Message:*",
      form.message,
    ].filter(Boolean);

    const text = encodeURIComponent(lines.join("\n"));
    const primaryNumber = CLASS_COORDINATORS[form.branch] || DEFAULT_WHATSAPP;
    const altNumber = ALT_CLASS_COORDINATORS[form.branch];
    const targetNumber = useAlt && altNumber ? altNumber : primaryNumber;
    const url = `https://wa.me/${targetNumber}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");

    toast.success("Opening WhatsApp...");
    setTimeout(() => {
      setDone(true);
      setForm({ name: "", email: "", phone: "", branch: "", program: "", message: "" });
      setSubmitting(false);
    }, 400);
  };

  const submit = (e) => submitToWhatsApp(e, false);
  const submitAlt = () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message first.");
      return;
    }
    submitToWhatsApp(null, true);
  };

  const sendViaEmail = () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message first.");
      return;
    }
    const subject = `New enquiry — Phoenix Dance Academy${form.branch ? ` (${form.branch})` : ""}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      form.branch ? `Branch of interest: ${form.branch}` : null,
      form.program ? `Program of interest: ${form.program}` : null,
      "",
      "Message:",
      form.message,
    ].filter((v) => v !== null).join("\n");
    const mailto = buildMailto(form.branch, subject, body);
    if (!mailto) {
      toast.error("Email enquiry is available for USA branch only.");
      return;
    }
    window.location.href = mailto;
    toast.success("Opening your email app...");
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <section className="relative h-[40vh] min-h-[320px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1518609571773-39b7d303a87b?w=2000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Get in touch</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Let's <span className="phoenix-text">connect.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-7">
          <h2 className="font-display text-3xl lg:text-4xl text-white mb-8">Send us a message</h2>
          {done ? (
            <div data-testid="contact-success" className="border border-[#25D366]/40 bg-[#25D366]/5 p-8">
              <p className="font-display text-2xl text-white mb-2">Almost there.</p>
              <p className="text-white/70">Please send the pre-filled message in the WhatsApp tab that just opened. Our team replies within 24 hours.</p>
              <button onClick={() => setDone(false)} data-testid="contact-send-another" className="mt-6 text-xs uppercase tracking-wider text-[#ff4500] font-semibold">
                Send another →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input testid="contact-name" placeholder="Full name" value={form.name} onChange={onChange("name")} required />
                <Input testid="contact-email" type="email" placeholder="Email" value={form.email} onChange={onChange("email")} required />
                <Input testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={onChange("phone")} />
                <Select testid="contact-branch" value={form.branch} onChange={onChange("branch")}>
                  <option value="">Branch of interest</option>
                  {BRANCHES.map((b) => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                </Select>
              </div>
              <Input testid="contact-program" placeholder="Program of interest (optional)" value={form.program} onChange={onChange("program")} />
              <Textarea testid="contact-message" rows={5} placeholder="How can we help?" value={form.message} onChange={onChange("message")} required />
              <div className="flex flex-wrap gap-3">
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-phoenix bg-[#25D366] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  {submitting ? "Opening WhatsApp..." : "Send via WhatsApp"}
                </button>
                {ALT_CLASS_COORDINATORS[form.branch] && (
                  <button
                    type="button"
                    onClick={submitAlt}
                    data-testid="contact-submit-alt"
                    className="btn-phoenix border border-[#25D366] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#25D366]"
                  >
                    <MessageCircle className="w-4 h-4" /> Alt WhatsApp
                  </button>
                )}
                {BRANCH_EMAILS[form.branch] && (
                  <button
                    type="button"
                    onClick={sendViaEmail}
                    data-testid="contact-submit-email"
                    className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" /> Send via Email
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Quick contact */}
        <div className="lg:col-span-5 space-y-4">
          <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">Reach out directly</h2>
          {BRANCHES.map((b) => (
            <div key={b.id} data-testid={`contact-branch-${b.id}`} className="border border-white/10 bg-[#0a0a0a] p-6 card-hover">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{b.flag}</span>
                  <div>
                    <p className="font-display text-xl text-white">{b.name}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700]">{b.city}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {b.locations.map((loc) => (
                  <div key={loc.id} data-testid={`contact-loc-${loc.id}`} className="border-t border-white/5 pt-4 first:border-t-0 first:pt-0">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-2">{loc.name}</p>
                    <p className="text-sm text-white/65 flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-white/40" />
                      <span>{loc.address}</span>
                    </p>
                    <a
                      href={`https://instagram.com/${loc.instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`contact-ig-${loc.id}`}
                      className="mt-3 inline-flex items-center gap-2 text-xs text-[#ffb700] hover:text-[#ff4500] transition-colors"
                    >
                      <Instagram className="w-3.5 h-3.5" /> @{loc.instagram}
                    </a>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap gap-2">
                {b.phone && (
                  <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="border border-white/15 text-white px-3 h-9 inline-flex items-center gap-2 text-xs uppercase tracking-wider hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {b.phone}
                  </a>
                )}
                {b.whatsapp && (
                  <a href={`https://wa.me/${b.whatsapp}`} target="_blank" rel="noreferrer" className="border border-white/15 text-white px-3 h-9 inline-flex items-center gap-2 text-xs uppercase tracking-wider hover:bg-[#25D366] hover:border-[#25D366] transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                )}
                {b.altWhatsapp && (
                  <a
                    href={`https://wa.me/${b.altWhatsapp}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-altwa-${b.id}`}
                    className="border border-[#25D366]/40 text-white px-3 h-9 inline-flex items-center gap-2 text-xs uppercase tracking-wider hover:bg-[#25D366] hover:border-[#25D366] transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Alt WhatsApp
                  </a>
                )}
                {BRANCH_EMAILS[b.name] && (
                  <a
                    href={`mailto:${BRANCH_EMAILS[b.name]}?subject=${encodeURIComponent(`Enquiry — Phoenix Dance Academy ${b.name}`)}`}
                    data-testid={`contact-email-${b.id}`}
                    className="border border-white/15 text-white px-3 h-9 inline-flex items-center gap-2 text-xs uppercase tracking-wider hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hours + Quick FAQ */}
      <section className="py-24 lg:py-32 bg-[#070707] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Studio Hours</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-[1] mb-8">
              When we're <span className="phoenix-text">moving.</span>
            </h2>
            <div className="border border-white/10">
              {[
                { d: "Monday – Friday", h: "10:00 AM — 9:00 PM" },
                { d: "Saturday", h: "9:00 AM — 8:00 PM" },
                { d: "Sunday", h: "11:00 AM — 6:00 PM" },
                { d: "Holidays", h: "By appointment" },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-4 border-b border-white/10 last:border-b-0">
                  <span className="text-sm uppercase tracking-wider text-white/70">{row.d}</span>
                  <span className="text-sm text-white">{row.h}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-4">Hours apply to all branches in their local timezone. Some classes run beyond standard hours.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Quick answers</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-[1] mb-8">
              Before you reach out.
            </h2>
            <div className="space-y-3">
              {[
                { q: "How fast do you respond?", a: "Within 24 hours on weekdays. Most replies come within a few hours." },
                { q: "Can I visit a studio without a trial?", a: "Absolutely. Drop by during studio hours — we'll show you around." },
                { q: "Do you offer corporate workshops?", a: "Yes. Reach out to us on WhatsApp with your team size, location, and timeline for a custom quote." },
              ].map((f, i) => (
                <div key={i} className="border border-white/10 bg-[#0a0a0a] p-5">
                  <p className="font-display text-lg text-white">{f.q}</p>
                  <p className="text-sm text-white/65 mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Input = ({ testid, ...props }) => (
  <input
    data-testid={testid}
    {...props}
    className="w-full bg-[#0a0a0a] border border-white/15 px-4 h-12 text-white placeholder:text-white/30 outline-none focus:border-[#ff4500] transition-colors"
  />
);

const Textarea = ({ testid, ...props }) => (
  <textarea
    data-testid={testid}
    {...props}
    className="w-full bg-[#0a0a0a] border border-white/15 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-[#ff4500] transition-colors resize-none"
  />
);

const Select = ({ testid, children, ...props }) => (
  <select
    data-testid={testid}
    {...props}
    className="w-full bg-[#0a0a0a] border border-white/15 px-4 h-12 text-white outline-none focus:border-[#ff4500] transition-colors appearance-none"
  >
    {children}
  </select>
);
